import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Link,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Check } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import MainAppBarRecruiter from "bundles/Layouts/MainAppBarRecruiter";
import Crane from "icons/crane.svg";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
    pl: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const ResendConfirmation = ({ userType }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [values, setValues] = useState({ email: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validate = (fieldValues = values) => {
    const temp = { ...errors };
    const req = intl.formatMessage({ id: "form_validation.required" });
    if ("email" in fieldValues) temp.email = fieldValues.email ? "" : req;
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    validate({ [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (!validate(values)) {
      setLoading(false);
      return;
    }
    sh.post(`/${i18nLocale}/${userType}/confirmation`, {
      authenticity_token,
      [userType.slice(0, -1)]: { email: values.email },
    })
      .then(() => setIsSuccess(true))
      .catch(() =>
        enqueueSnackbar(intl.formatMessage({ id: "flash.unknown" }), {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(230, 100%, 99%)" }}>
      <MainAppBarRecruiter />

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          px: "1.5rem",
          pb: "1rem",
          minHeight: "calc(100vh - 68px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "64rem",
            alignItems: "center",
          }}
        >
          {/* ── Form card ── */}
          <Box
            sx={{
              width: isDesktop ? "50%" : "100%",
              bgcolor: "hsl(0, 0%, 100%)",
              borderRadius: "1rem",
              boxShadow:
                "0 4px 12px -2px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.04)",
              p: isDesktop ? "2.5rem" : "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            {/* Title */}
            <h1
              style={{
                fontFamily:
                  "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                fontSize: isDesktop ? "1.875rem" : "1.5rem",
                lineHeight: 1.2,
                color: "hsl(222.2, 84%, 4.9%)",
                margin: 0,
              }}
            >
              <FormattedMessage id="devise.confirmation.email_confirmation" />
            </h1>

            {/* Subtitle */}
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "hsl(220, 10%, 46%)",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                lineHeight: 1.5,
              }}
            >
              <FormattedMessage id="devise.confirmation.confirmation_instructions" />
            </Typography>

            {!isSuccess ? (
              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <TextField
                  fullWidth
                  required
                  type="email"
                  variant="outlined"
                  name="email"
                  onChange={handleInputValue}
                  onBlur={handleInputValue}
                  sx={inputSx}
                  {...(errors.email && {
                    error: true,
                    helperText: errors.email,
                  })}
                  label={<FormattedMessage id="devise.signup.email" />}
                />

                <Button
                  fullWidth
                  type="submit"
                  disabled={loading}
                  sx={{
                    bgcolor: "hsl(153 68% 25%)",
                    color: "white",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    px: "1.5rem",
                    py: "0.75rem",
                    borderRadius: "0.75rem",
                    textTransform: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    mt: "0.75rem",
                    "&:hover": {
                      bgcolor: "hsl(153 68% 25% / 0.9)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "hsl(153 68% 25% / 0.3)",
                      color: "hsl(0 0% 100% / 0.6)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <FormattedMessage id="send" />
                  )}
                </Button>
              </form>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  p: "1rem",
                  borderRadius: "0.75rem",
                  bgcolor: "hsl(153 68% 25% / 0.08)",
                }}
              >
                <Check size={20} style={{ color: "hsl(153, 68%, 25%)" }} />
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "hsl(153, 68%, 25%)",
                    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    lineHeight: 1.5,
                  }}
                >
                  <FormattedMessage id="devise.confirmation.confirmation_success" />
                </Typography>
              </Box>
            )}

            {/* Back to login */}
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "hsl(215.4, 16.3%, 46.9%)",
                fontFamily: "'Segoe UI', system-ui, sans-serif",
              }}
            >
              <Link
                href={`/${i18nLocale}/${userType}/sign_in`}
                sx={{
                  color: "hsl(153 68% 25%)",
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <FormattedMessage id="devise.password.back_to_login" />
              </Link>
            </Typography>
          </Box>

          {/* ── Crane illustration ── */}
          {isDesktop && (
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                overflow: "hidden",
              }}
            >
              <img
                src={Crane}
                alt="Crane illustration"
                style={{
                  height: "100vh",
                  maxHeight: "950px",
                  objectFit: "contain",
                  transform: "translateX(48px) translateY(-5%)",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResendConfirmation;
